import React, { useEffect, useState } from 'react';
import FilterPanel from './FilterPanel';
import { useLocation, useNavigate } from 'react-router-dom';
import FlightLoadingScreen from './FlightLoadingScreen';
import NoFlights from './NoFlightsFound';
import RenderTopFlights from './FlightsAtTopComponent';
import BookingBenefitsCard from './BookingBenefitsCard';

const extractIATACode = (fullString) => {
  const match = fullString.match(/\(([^)]+)\)$/);
  return match ? match[1] : '';
};

const FlightResults = () => {
  const [data, setData] = useState([]);
  const [otherFlightsData, setOtherFlightsData] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [origResult, setOrigResult] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [maxPrice, setMaxPrice] = useState('');
  const [stops, setStops] = useState('');
  const [airline, setAirline] = useState('');
  const [selectedDepartureFlight, setSelectedDepartureFlight] = useState(null);
  const [arrivalOptions, setArrivalOptions] = useState([]);
  const [selectedArrivalFlight, setSelectedArrivalFlight] = useState(null);
  const [currency, setCurrency] = useState('USD');
  const [currencyMultiplier, setCurrencyMultiplier] = useState(1);
  const [currencySymbol, setCurrencySymbol] = useState('$');

  const location = useLocation();
  const navigate = useNavigate();
  const {
    from,
    to,
    departureDate,
    arrivalDate,
    tripType,
    adults,
    children,
    infants,
    cabinClass,
  } = location.state || {};

  const delay = (ms) => new Promise((res) => setTimeout(res, ms));

  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchFlights = async () => {
      if (!from || !to || !departureDate) return;

      setLoading(true);
      await delay(1500); // Simulate wait

      try {
        const basePayload = {
          engine: 'google_flights',
          origin: extractIATACode(from),
          destination: extractIATACode(to),
          depart_date: departureDate,
          currency: 'USD',
          hl: 'en',
          type: tripType === 'One Way' ? 2 : 1,
          passangers: adults,
          children: children,
          infants_in_seat: infants,
          travel_class: cabinClass,
        };

        if (tripType === 'Round Trip') {
          basePayload.return_date = arrivalDate;
        }

        const response = await fetch('https://booktrip4me.com/api/get-flights.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(basePayload),
        });
        console.log(basePayload);

        const result = await response.json();

        if (!result.best_flights && !result.other_flights) navigate('/noflights');

        setFilteredResults([...(result.best_flights || []), ...(result.other_flights || [])]);
        setOrigResult(result);

        setData(result.best_flights || []);
        setOtherFlightsData(result.other_flights || []);
        console.log(result);
        console.log(departureDate);
      } catch (error) {
        console.error('Error fetching flights:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFlights();
  }, [
    from,
    to,
    departureDate,
    arrivalDate,
    tripType,
    adults,
    children,
    infants,
    cabinClass,
    navigate,
  ]);

  const handleSelectFlight = async (flight, direction = 'departure') => {
    if (tripType === 'Round Trip') {
      if (direction === 'departure') {
        setSelectedDepartureFlight(flight);
        setLoading(true);

        try {
          const response = await fetch('https://booktrip4me.com/api/get-flights.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              engine: 'google_flights',
              origin: extractIATACode(from),
              destination: extractIATACode(to),
              depart_date: departureDate,
              return_date: arrivalDate,
              currency: 'USD',
              hl: 'en',
              departure_token: flight.departure_token,
              type: 1,
              passangers: adults,
              children: children,
              infants_in_seat: infants,
              travel_class: cabinClass,
            }),
          });
          console.log(
            JSON.stringify({
              engine: 'google_flights',
              origin: extractIATACode(from),
              destination: extractIATACode(to),
              depart_date: departureDate,
              return_date: arrivalDate,
              currency: 'USD',
              hl: 'en',
              departure_token: flight.departure_token,
              type: 1,
              passangers: adults,
              children: children,
              infants_in_seat: infants,
              travel_class: cabinClass,
            })
          );
          const result = await response.json();

          if (!result.best_flights && !result.other_flights) navigate('/noflights');

          setFilteredResults([
            ...(result.best_flights || []),
            ...(result.other_flights || []),
          ]);
          setOrigResult(result);
        } catch (err) {
          console.error('Failed to fetch arrival flights:', err);
        } finally {
          setLoading(false);
        }
      } else if (direction === 'arrival') {
        setLoading(true);
        setSelectedArrivalFlight(flight);

        setTimeout(() => {
          setLoading(false);
          navigate('/checkout', {
            state: {
              departureFlight: selectedDepartureFlight,
              arrivalFlight: flight,
              adults: adults,
              children: children,
              infants: infants,
            },
          });
        }, 2000);
      }
    } else {
      // One Way Trip
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
        navigate('/checkout', {
          state: {
            departureFlight: flight,
            adults: adults,
            children: children,
            infants: infants,
          },
        });
      }, 2000);
    }
  };

  const toggleExpand = (id) => {
    setExpanded(expanded === id ? null : id);
  };

  const renderFlightSegment = (flight, idx) => (
    <div
      key={idx}
      className="border-b border-gray-100 pb-4 mb-4 grid grid-cols-1 sm:grid-cols-4 gap-4"
    >
      <div className="flex items-center gap-3">
        <img src={flight.airline_logo} alt={flight.airline} className="w-12 h-10 rounded-lg" />
        <div>
          <div className="font-semibold text-gray-800">
            {flight.airline} • {flight.flight_number}
          </div>
          <div className="text-sm text-gray-500">{flight.airplane}</div>
        </div>
      </div>
      <div className="text-center">
        <div className="font-semibold text-gray-900">
          {new Date(flight.departure_airport.time).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
        <div className="text-sm text-gray-600">
          {flight.departure_airport.name} ({flight.departure_airport.id})
        </div>
      </div>
      <div className="text-center">
        <div className="text-sm text-gray-500">
          ⏱ {Math.floor(flight.duration / 60)}h {flight.duration % 60}m
        </div>
        {flight.overnight && (
          <div className="text-xs text-red-500 font-medium mt-1">Overnight</div>
        )}
      </div>
      <div className="text-center">
        <div className="font-semibold text-gray-900">
          {new Date(flight.arrival_airport.time).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
        <div className="text-sm text-gray-600">
          {flight.arrival_airport.name} ({flight.arrival_airport.id})
        </div>
      </div>
      <div className="text-xs mt-2 text-gray-500 col-span-full">
        {flight.extensions.join(' • ')}
      </div>
    </div>
  );

  const renderFlightCard = (result, index, type) => {
    const isExpanded = expanded === `${type}-${index}`;
    const allFlights = result.flights;
    const first = allFlights[0];
    const last = allFlights[allFlights.length - 1];
    const durationMin =
      (new Date(last.arrival_airport.time) - new Date(first.departure_airport.time)) / 60000;
    const hours = Math.floor(durationMin / 60);
    const minutes = durationMin % 60;
    const stopsCount = allFlights.length - 1;

    return (
      <div key={`${type}-${index}`} className="mb-4">
        {/* Desktop Card */}
        <div className="hidden sm:block bg-white shadow-md rounded-2xl border border-gray-100 mb-4 p-5 md:p-6">
          {/* Header */}
          <div className="flex flex-row flex-wrap sm:flex-nowrap items-center justify-between gap-4">
            {/* Airline Info */}
            <div className="flex items-center gap-3">
              <img
                src={allFlights[0].airline_logo}
                alt={allFlights[0].airline}
                className="w-12 h-10 rounded-xl"
              />
              <div>
                <div className="font-semibold text-gray-900 text-base">
                  {allFlights[0].airline}
                </div>
                <div className="text-sm text-gray-500">
                  {allFlights[0].flight_number}
                </div>
              </div>
            </div>

            {/* Departure */}
            <div className="text-center min-w-[100px]">
              <div className="text-xl font-bold text-gray-900">
                {new Date(first.departure_airport.time).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                })}
              </div>
              <div className="text-sm text-gray-600">
                {type === 'arrival'
                  ? origResult.airports[0].arrival[0].city
                  : origResult.airports[0].departure[0].city}
              </div>
            </div>

            {/* Duration */}
            <div className="text-center text-sm text-gray-500 min-w-[140px]">
              <div className="font-medium text-gray-700">
                {hours}h {minutes}m
              </div>
              <div className="text-xs text-gray-500">
                {origResult.airports[0].departure[0].airport.id} -{' '}
                {origResult.airports[0].arrival[0].airport.id}
              </div>
              <div className="flex items-center gap-2 mt-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full" />
                <div className="flex-grow border-t border-dotted border-gray-300" />
                <div className="w-2 h-2 bg-gray-400 rounded-full" />
              </div>
              <div className="text-xs mt-1 text-gray-600">
                {stopsCount === 0 ? 'Non-stop' : `${stopsCount} stop${stopsCount > 1 ? 's' : ''}`}
              </div>
              {result.overnight && (
                <div className="text-xs text-red-500 font-semibold mt-1">
                  Overnight
                </div>
              )}
            </div>

            {/* Arrival */}
            <div className="text-center min-w-[100px]">
              <div className="text-xl font-bold text-gray-900">
                {new Date(last.arrival_airport.time).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                })}
              </div>
              <div className="text-sm text-gray-600">
                {type === 'arrival'
                  ? origResult.airports[0].departure[0].city
                  : origResult.airports[0].arrival[0].city}
              </div>
            </div>

            {/* Price */}
            <div className="text-right min-w-[110px]">
              <div className="text-xl font-bold text-emerald-600">
                {currencySymbol}
                {(result.price * currencyMultiplier).toFixed(2)}
              </div>
              <div className="text-[11px] text-gray-500 mt-1">
                Taxes & fees included (approx)
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="mt-4 flex flex-row justify-between items-center gap-3">
            {allFlights.length > 0 && (
              <button
                onClick={() => toggleExpand(`${type}-${index}`)}
                className="text-sm text-white bg-gradient-to-r from-teal-500 to-teal-600 px-5 py-2 rounded-full shadow-md hover:shadow-lg hover:from-teal-600 hover:to-teal-700 transition"
              >
                {isExpanded ? '▲ Hide details' : '▼ View details'}
              </button>
            )}

            <button
              onClick={() => handleSelectFlight(result, type)}
              className="text-sm bg-gradient-to-r from-orange-400 to-orange-500 text-white px-5 py-2 rounded-full shadow-md hover:from-orange-500 hover:to-orange-600 transition"
            >
              Select
            </button>
          </div>

          {/* Details */}
          <div
            className={`transition-all duration-300 ease-in-out overflow-hidden ${
              isExpanded ? 'max-h-[2000px] mt-4' : 'max-h-0'
            }`}
          >
            {allFlights.map((flight, idx) => (
              <div key={idx} className="border-t border-gray-100 pt-4">
                {renderFlightSegment(flight, idx)}
              </div>
            ))}
          </div>
        </div>

        {/* Mobile Card */}
        <div className="block sm:hidden bg-white shadow-md rounded-2xl border border-gray-100 p-3 text-xs">
          {/* Airline Row */}
          <div className="flex items-center gap-3 mb-2">
            <img
              src={allFlights[0].airline_logo}
              alt={allFlights[0].airline}
              className="w-8 h-8 rounded-lg"
            />
            <div>
              <div className="font-semibold text-sm text-gray-900">
                {allFlights[0].airline}
              </div>
              <div className="text-gray-500 text-[11px]">
                {allFlights[0].flight_number}
              </div>
            </div>
          </div>

          {/* Time Row */}
          <div className="flex items-center justify-between gap-2 text-[11px]">
            <div className="text-left">
              <div className="font-bold text-sm text-gray-900">
                {new Date(first.departure_airport.time).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                })}
              </div>
              <div className="text-gray-600">
                {origResult.airports[0].departure[0].city}
              </div>
            </div>

            <div className="flex flex-col items-center flex-1 text-center text-gray-500">
              <div className="text-xs">
                {hours}h {minutes}m |{' '}
                {origResult.airports[0].departure[0].airport.id} -{' '}
                {origResult.airports[0].arrival[0].airport.id}
              </div>
              <div className="flex items-center text-xs w-full gap-1 mt-1">
                <div className="w-1.5 h-1.5 bg-gray-500 rounded-full" />
                <div className="border-t border-dotted border-gray-400 flex-grow" />
                <div className="w-1.5 h-1.5 bg-gray-500 rounded-full" />
              </div>

              <div className="mt-1">
                {stopsCount === 0 ? 'Non-stop' : `${stopsCount} stop${stopsCount > 1 ? 's' : ''}`}
              </div>
              {result.overnight && (
                <div className="text-red-500 font-semibold mt-0.5">Overnight</div>
              )}
            </div>

            <div className="text-right">
              <div className="font-bold text-sm text-gray-900">
                {new Date(last.arrival_airport.time).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                })}
              </div>
              <div className="text-gray-600">
                {origResult.airports[0].arrival[0].city}
              </div>
            </div>
          </div>

          {/* Price + Actions */}
          <div className="mt-3 flex items-center justify-between gap-2">
            {allFlights.length > 0 && (
              <button
                onClick={() => toggleExpand(`${type}-${index}`)}
                className="text-[11px] bg-teal-500 text-white px-3 py-1 rounded-full shadow-sm"
              >
                {isExpanded ? '▲ Details' : '▼ Details'}
              </button>
            )}

            <div className="text-right">
              <div className="text-sm font-bold text-emerald-600">
                {currencySymbol}
                {(result.price * currencyMultiplier).toFixed(2)}
              </div>
            </div>

            <button
              onClick={() => handleSelectFlight(result, type)}
              className="text-[11px] bg-orange-500 text-white px-3 py-1 rounded-full shadow-sm"
            >
              Select
            </button>
          </div>

          {/* Details */}
          <div
            className={`transition-all duration-300 ease-in-out overflow-hidden ${
              isExpanded ? 'max-h-[2000px] mt-3' : 'max-h-0'
            }`}
          >
            {allFlights.map((flight, idx) => (
              <div key={idx} className="border-t border-gray-100 pt-3">
                {renderFlightSegment(flight, idx)}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const filteredData = data.filter((result) => {
    const totalStops = result.flights.length - 1;
    const matchesPrice = maxPrice ? result.price <= parseInt(maxPrice) : true;
    const matchesStops = stops
      ? stops === '2'
        ? totalStops >= 2
        : totalStops === parseInt(stops)
      : true;
    const matchesAirline = airline
      ? result.flights.some((f) =>
          f.airline.toLowerCase().includes(airline.toLowerCase())
        )
      : true;
    return matchesPrice && matchesStops && matchesAirline;
  });

  if (loading) return <FlightLoadingScreen />;

  return (
    <div className="bg-[#F5F7FA] min-h-screen pb-10">
      {/* Top re-search bar section (already themed elsewhere) */}
      <RenderTopFlights
        defaultFrom={from}
        defaultTo={to}
        defaultDepartureDate={departureDate}
        defaultArrivalDate={arrivalDate}
        defaultTravelers={adults}
        defaultChildren={children}
        defaultInfants={infants}
        defaultTravelClass={cabinClass}
        defaultTripType={tripType}
      />

      <div className="max-w-6xl mx-auto mt-4 px-3 md:px-0">
        {/* Header row – can add summary later if you want */}
        {/* <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg md:text-2xl font-semibold text-gray-800">
            Available Flight Options
          </h2>
        </div> */}

        <div className="flex flex-col md:flex-row gap-4">
          {/* Filter Panel */}
          <div className="w-full md:w-1/4">
            <div className="bg-white rounded-2xl shadow-md border border-gray-100">
              <FilterPanel
                allFlights={[...data, ...otherFlightsData]}
                onFilter={setFilteredResults}
              />
            </div>
          </div>

          {/* Flights List */}
          <div className="w-full md:w-3/4 space-y-4">
            {/* ONE WAY */}
            {tripType === 'One Way' && (
              <>
                {(filteredResults.length ? filteredResults : data).map(
                  (result, index) => {
                    const elements = [];
                    elements.push(renderFlightCard(result, index, 'departure'));

                    const insertAfter = [3, 4, 5];
                    let cumulative = insertAfter.reduce((acc, cur, i) => {
                      acc.push((acc[i - 1] || 0) + cur);
                      return acc;
                    }, []);

                    if (cumulative.includes(index + 1)) {
                      elements.push(
                        <BookingBenefitsCard key={`promo-${index}`} />
                      );
                    }

                    return elements;
                  }
                )}

                {filteredResults.length === 0 && (
                  <div className="bg-white shadow-md rounded-2xl p-5 border border-amber-200">
                    <p className="text-sm md:text-base font-semibold text-gray-800">
                      No flights found for these filter options. Please adjust
                      your filters to see more results.
                    </p>
                  </div>
                )}
              </>
            )}

            {/* ROUND TRIP – SELECT DEPARTURE FIRST */}
            {tripType === 'Round Trip' && !selectedDepartureFlight && (
              <>
                {(filteredResults.length ? filteredResults : data).map(
                  (result, index) => {
                    const elements = [];
                    elements.push(renderFlightCard(result, index, 'departure'));

                    const insertAfter = [3, 4, 5];
                    let cumulative = insertAfter.reduce((acc, cur, i) => {
                      acc.push((acc[i - 1] || 0) + cur);
                      return acc;
                    }, []);

                    if (cumulative.includes(index + 1)) {
                      elements.push(
                        <BookingBenefitsCard key={`promo-${index}`} />
                      );
                    }

                    return elements;
                  }
                )}

                {filteredResults.length === 0 && (
                  <div className="bg-white shadow-md rounded-2xl p-5 border border-amber-200">
                    <p className="text-sm md:text-base font-semibold text-gray-800">
                      No flights found for these filter options. Please adjust
                      your filters to see more results.
                    </p>
                  </div>
                )}
              </>
            )}

            {/* ROUND TRIP – RETURN FLIGHT SELECTION */}
            {tripType === 'Round Trip' && selectedDepartureFlight && (
              <>
                <h3 className="text-lg md:text-xl font-semibold mt-2 mb-2 text-gray-800">
                  Select your return flight
                </h3>

                {(filteredResults.length ? filteredResults : data).map(
                  (result, index) => {
                    const elements = [];
                    elements.push(renderFlightCard(result, index, 'arrival'));

                    const insertAfter = [3, 4, 5];
                    let cumulative = insertAfter.reduce((acc, cur, i) => {
                      acc.push((acc[i - 1] || 0) + cur);
                      return acc;
                    }, []);

                    if (cumulative.includes(index + 1)) {
                      elements.push(
                        <BookingBenefitsCard
                          key={`promo-arrival-${index}`}
                        />
                      );
                    }

                    return elements;
                  }
                )}

                {filteredResults.length === 0 && (
                  <div className="bg-white shadow-md rounded-2xl p-5 border border-amber-200">
                    <p className="text-sm md:text-base font-semibold text-gray-800">
                      No flights found for these filter options. Please adjust
                      your filters to see more results.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlightResults;
